import { useState, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Camera, SpinnerGap, Check, Warning, ArrowRight, Image as ImageIcon, X, FileXls, FilePdf, FileCsv, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import { IngredientCombobox } from "@/components/IngredientCombobox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACCEPTED_TYPES = {
  "image/jpeg": "image", "image/png": "image", "image/webp": "image",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "excel",
  "application/vnd.ms-excel": "excel",
  "application/pdf": "pdf",
  "text/csv": "csv",
};

const FILE_ICONS = { image: ImageIcon, excel: FileXls, pdf: FilePdf, csv: FileCsv };

export default function RecipeImportAI({ open, onClose, ingredients, onImported }) {
  const { t } = useLanguage();
  const [step, setStep] = useState("upload");
  const [filePreview, setFilePreview] = useState(null);
  const [fileBase64, setFileBase64] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [recipeData, setRecipeData] = useState(null);
  const [editData, setEditData] = useState(null);
  const [localIngredients, setLocalIngredients] = useState([]);
  const [creatingIdx, setCreatingIdx] = useState(null);
  const [newIngDraft, setNewIngDraft] = useState({ name: "", quantity_purchased: "1", unit: "kg", purchase_price: "" });
  const [savingNewIng, setSavingNewIng] = useState(false);
  const fileRef = useRef(null);

  const allIngredients = [...ingredients, ...localIngredients];

  const reset = () => {
    setStep("upload"); setFilePreview(null); setFileBase64(null);
    setFileType(null); setFileName(null); setRecipeData(null); setEditData(null);
    setLocalIngredients([]); setCreatingIdx(null);
  };
  const handleClose = () => { reset(); onClose(); };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const detectedType = ACCEPTED_TYPES[file.type];
    if (!detectedType) {
      toast.error("Formato não suportado. Use: imagens, Excel (.xlsx), PDF ou CSV.");
      return;
    }
    if (file.size > 15 * 1024 * 1024) { toast.error("Arquivo muito grande (máx 15MB)"); return; }

    setFileType(detectedType);
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (ev) => {
      setFileBase64(ev.target.result);
      if (detectedType === "image") {
        setFilePreview(ev.target.result);
      } else {
        setFilePreview(null);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleScan = async () => {
    if (!fileBase64) return;
    setStep("scanning");
    try {
      const payload = fileType === "image"
        ? { image_base64: fileBase64, file_type: "image" }
        : { file_base64: fileBase64, file_type: fileType, file_name: fileName };

      const { data } = await axios.post(`${API}/technical-sheets/import-ai`, payload);
      if (data.error) { toast.error(data.error); setStep("upload"); return; }
      if (!data.nome && (!data.ingredientes || data.ingredientes.length === 0)) {
        toast.error("Não foi possível extrair a receita. Tente outro arquivo ou foto com melhor qualidade.");
        setStep("upload");
        return;
      }
      setRecipeData(data);
      setEditData({
        name: data.nome || "",
        yield_quantity: data.rendimento_quantidade || 1,
        yield_unit: data.rendimento_unidade || "unidades",
        modo_preparo: data.modo_preparo || "",
        ingredientes: (data.ingredientes || []).map((ing, idx) => ({
          key: idx, nome: ing.nome || "", quantidade: ing.quantidade || 0,
          unidade: ing.unidade || "g", matched_id: ing.matched_id || null,
          matched_name: ing.matched_name || null, matched_cost: ing.matched_cost || 0,
          fator_coccao: ing.fator_coccao || 1.0, peso_equivalente_g: ing.peso_equivalente_g || null,
        })),
      });
      setStep("preview");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao processar arquivo");
      setStep("upload");
    }
  };

  const handleIngredientLink = (idx, ingredientId) => {
    if (!editData) return;
    const updated = { ...editData, ingredientes: [...editData.ingredientes] };
    const ing = { ...updated.ingredientes[idx] };
    if (!ingredientId || ingredientId === "none") {
      ing.matched_id = null; ing.matched_name = null; ing.matched_cost = 0;
    } else {
      const dbIng = allIngredients.find(i => i.id === ingredientId);
      if (dbIng) {
        ing.matched_id = dbIng.id; ing.matched_name = dbIng.name;
        ing.matched_cost = dbIng.cost_per_unit; ing.fator_coccao = dbIng.fator_coccao_padrao || 1.0;
        ing.peso_equivalente_g = dbIng.peso_equivalente_g || null;
      }
    }
    updated.ingredientes[idx] = ing;
    setEditData(updated);
  };

  const openCreateIngredient = (idx) => {
    const ing = editData?.ingredientes[idx];
    setNewIngDraft({
      name: ing?.nome || "",
      quantity_purchased: "1",
      unit: ["kg", "g", "l", "ml", "unidades", "maço"].includes(ing?.unidade) ? ing.unidade : "kg",
      purchase_price: "",
    });
    setCreatingIdx(idx);
  };

  const handleCreateIngredientInPlace = async () => {
    if (!newIngDraft.name.trim()) { toast.error("Informe o nome do insumo"); return; }
    setSavingNewIng(true);
    try {
      const price = parseFloat(newIngDraft.purchase_price) || 0;
      const { data } = await axios.post(`${API}/ingredients`, {
        name: newIngDraft.name.trim(),
        quantity_purchased: parseFloat(newIngDraft.quantity_purchased) || 1,
        unit: newIngDraft.unit,
        purchase_price: price,
      });
      setLocalIngredients(prev => [...prev, data]);
      handleIngredientLinkWithData(creatingIdx, data);
      setCreatingIdx(null);
      toast.success(`Insumo "${data.name}" criado e vinculado!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao criar insumo");
    } finally {
      setSavingNewIng(false);
    }
  };

  const handleIngredientLinkWithData = (idx, dbIng) => {
    setEditData(prev => {
      if (!prev) return prev;
      const updated = { ...prev, ingredientes: [...prev.ingredientes] };
      updated.ingredientes[idx] = {
        ...updated.ingredientes[idx],
        matched_id: dbIng.id, matched_name: dbIng.name,
        matched_cost: dbIng.cost_per_unit, fator_coccao: dbIng.fator_coccao_padrao || 1.0,
        peso_equivalente_g: dbIng.peso_equivalente_g || null,
      };
      return updated;
    });
  };

  const handleConfirm = async () => {
    if (!editData) return;
    const unlinked = editData.ingredientes.filter(i => !i.matched_id);
    setStep("saving");
    try {
      const sheetIngredients = editData.ingredientes.map(ing => {
        const dbIng = allIngredients.find(i => i.id === ing.matched_id);
        const qty = parseFloat(ing.quantidade) || 0;
        const fc = ing.fator_coccao || 1.0;
        const cost = (dbIng?.cost_per_unit || 0) * qty;
        return {
          ingredient_id: ing.matched_id || "", ingredient_name: ing.matched_name || ing.nome,
          quantity_used: qty, quantidade_bruta: qty, quantidade_liquida: qty * fc,
          fator_coccao: fc, unit: ing.unidade || dbIng?.unit || "g",
          item_cost: Math.round(cost * 100) / 100, is_qb: false,
          peso_equivalente_g: ing.peso_equivalente_g, is_sub_recipe: false, sub_recipe_id: null,
        };
      });
      await axios.post(`${API}/technical-sheets`, {
        name: editData.name, yield_quantity: parseFloat(editData.yield_quantity) || 1,
        yield_unit: editData.yield_unit, modo_preparo: editData.modo_preparo || null,
        formula_padeiro: false, ingrediente_base_id: null, gramatura_porcao: null, foto_url: null,
        ingredients: sheetIngredients,
      });
      if (unlinked.length > 0) {
        toast.success(`Ficha importada! ${unlinked.length} insumo(s) criado(s) automaticamente — o SousChef está estimando os preços.`);
      } else {
        toast.success("Ficha técnica importada com sucesso!");
      }
      onImported();
      handleClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar ficha");
      setStep("preview");
    }
  };

  const matchedCount = editData?.ingredientes.filter(i => i.matched_id).length || 0;
  const totalCount = editData?.ingredientes.length || 0;
  const FileIcon = FILE_ICONS[fileType] || ImageIcon;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="sm:max-w-[680px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold tracking-tight flex items-center gap-2">
            <Camera size={22} /> Importar Receita com IA
          </DialogTitle>
          <DialogDescription className="sr-only">Importar ficha técnica a partir de foto ou arquivo</DialogDescription>
        </DialogHeader>

        {/* STEP 1: Upload */}
        {step === "upload" && (
          <div className="space-y-4 mt-2">
            <p className="text-sm text-zinc-500">
              Faça upload de uma <strong>foto</strong>, <strong>planilha Excel</strong>, <strong>PDF</strong> ou <strong>CSV</strong> contendo a receita. A IA vai extrair os dados e criar uma ficha no formato do app.
            </p>

            {fileBase64 ? (
              <div className="relative border border-zinc-200 rounded-lg p-4">
                <Button variant="ghost" size="icon" className="absolute top-2 right-2 h-7 w-7 bg-white/80 hover:bg-white" onClick={() => { setFileBase64(null); setFilePreview(null); setFileName(null); setFileType(null); }}>
                  <X size={14} />
                </Button>
                {filePreview ? (
                  <img src={filePreview} alt="Preview" className="w-full max-h-56 object-contain rounded" />
                ) : (
                  <div className="flex items-center gap-3 py-4">
                    <FileIcon size={40} className="text-zinc-400" />
                    <div>
                      <p className="font-medium text-zinc-800">{fileName}</p>
                      <p className="text-xs text-zinc-400 mt-0.5">
                        {fileType === "excel" ? "Planilha Excel" : fileType === "pdf" ? "Documento PDF" : fileType === "csv" ? "Arquivo CSV" : "Imagem"}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div
                className="border-2 border-dashed border-zinc-300 rounded-lg p-8 text-center cursor-pointer hover:border-zinc-400 transition-colors"
                onClick={() => fileRef.current?.click()}
                data-testid="recipe-import-dropzone"
              >
                <ImageIcon size={48} className="mx-auto text-zinc-300 mb-3" />
                <p className="text-zinc-600 font-medium">Clique para selecionar um arquivo</p>
                <p className="text-xs text-zinc-400 mt-1">Imagens (JPG, PNG) · Excel (.xlsx) · PDF · CSV</p>
              </div>
            )}

            <input ref={fileRef} type="file" accept="image/*,.xlsx,.xls,.pdf,.csv" className="hidden" onChange={handleFileSelect} data-testid="recipe-import-file-input" />
            <Button onClick={handleScan} disabled={!fileBase64} className="w-full bg-zinc-950 text-white hover:bg-zinc-800" data-testid="recipe-import-scan-btn">
              <Camera size={16} className="mr-2" /> Analisar com IA
            </Button>
          </div>
        )}

        {/* STEP 2: Scanning */}
        {step === "scanning" && (
          <div className="flex flex-col items-center justify-center py-12">
            <SpinnerGap size={48} className="text-zinc-400 animate-spin mb-4" />
            <p className="text-zinc-600 font-medium">Analisando {fileType === "excel" ? "planilha" : fileType === "pdf" ? "documento" : fileType === "csv" ? "arquivo" : "receita"}...</p>
            <p className="text-xs text-zinc-400 mt-1">A IA está extraindo ingredientes e instruções</p>
          </div>
        )}

        {/* STEP 3: Preview */}
        {step === "preview" && editData && (
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Nome da Receita</Label>
              <Input value={editData.name} onChange={(e) => setEditData({ ...editData, name: e.target.value })} className="mt-1.5" data-testid="recipe-import-name" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Rendimento</Label>
                <Input type="number" value={editData.yield_quantity} onChange={(e) => setEditData({ ...editData, yield_quantity: e.target.value })} className="mt-1.5" data-testid="recipe-import-yield" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Unidade</Label>
                <Select value={editData.yield_unit} onValueChange={(v) => setEditData({ ...editData, yield_unit: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["unidades", "porções", "kg", "g", "l", "ml"].map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">
                Ingredientes ({matchedCount}/{totalCount} vinculados)
              </Label>
              <div className="mt-2 space-y-2 max-h-72 overflow-y-auto">
                {editData.ingredientes.map((ing, idx) => (
                  <div key={idx} className={`p-2 rounded border text-sm ${ing.matched_id ? "border-emerald-200 bg-emerald-50/50" : "border-amber-200 bg-amber-50/50"}`}>
                    <div className="flex items-center gap-2">
                      <div className="flex-shrink-0 w-5">
                        {ing.matched_id ? <Check size={16} className="text-emerald-600" /> : <Warning size={16} className="text-amber-500" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{ing.nome}</p>
                        <p className="text-xs text-zinc-500">{ing.quantidade} {ing.unidade}</p>
                      </div>
                      <ArrowRight size={14} className="text-zinc-300 flex-shrink-0" />
                      <div className="w-44">
                        <IngredientCombobox
                          items={allIngredients.map(dbIng => ({ id: dbIng.id, label: `${dbIng.name} (${dbIng.unit})`, estimated: dbIng.is_estimated }))}
                          value={ing.matched_id}
                          onSelect={(v) => handleIngredientLink(idx, v)}
                          placeholder="Vincular..."
                          allowNone
                          testId={`recipe-import-link-${idx}`}
                          buttonClassName="h-8 text-xs"
                        />
                      </div>
                      {!ing.matched_id && (
                        <Button type="button" variant="outline" size="sm" className="h-8 text-[11px] px-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50 shrink-0" onClick={() => openCreateIngredient(idx)} data-testid={`recipe-import-create-${idx}`}>
                          <Plus size={12} className="mr-1" /> Criar Insumo
                        </Button>
                      )}
                    </div>
                    {creatingIdx === idx && (
                      <div className="mt-2 p-2 bg-white border border-emerald-200 rounded space-y-2" data-testid="recipe-import-inline-create">
                        <p className="text-[10px] font-bold text-emerald-800 uppercase tracking-wide">Novo insumo (sem sair da importação)</p>
                        <div className="grid grid-cols-12 gap-2">
                          <Input className="col-span-4 h-8 text-xs" placeholder="Nome *" value={newIngDraft.name} onChange={(e) => setNewIngDraft({ ...newIngDraft, name: e.target.value })} data-testid="inplace-ing-name" />
                          <Input className="col-span-2 h-8 text-xs" type="number" step="0.001" placeholder="Qtd" value={newIngDraft.quantity_purchased} onChange={(e) => setNewIngDraft({ ...newIngDraft, quantity_purchased: e.target.value })} data-testid="inplace-ing-qty" />
                          <div className="col-span-2">
                            <Select value={newIngDraft.unit} onValueChange={(v) => setNewIngDraft({ ...newIngDraft, unit: v })}>
                              <SelectTrigger className="h-8 text-xs" data-testid="inplace-ing-unit"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {["kg", "g", "l", "ml", "unidades", "maço"].map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <Input className="col-span-2 h-8 text-xs" type="number" step="0.01" placeholder="R$ (vazio = IA estima)" value={newIngDraft.purchase_price} onChange={(e) => setNewIngDraft({ ...newIngDraft, purchase_price: e.target.value })} data-testid="inplace-ing-price" />
                          <div className="col-span-2 flex gap-1">
                            <Button type="button" size="sm" className="h-8 flex-1 bg-emerald-700 hover:bg-emerald-800 text-white text-xs" onClick={handleCreateIngredientInPlace} disabled={savingNewIng} data-testid="inplace-ing-save">
                              {savingNewIng ? "..." : "Salvar"}
                            </Button>
                            <Button type="button" size="sm" variant="ghost" className="h-8 px-2" onClick={() => setCreatingIdx(null)} data-testid="inplace-ing-cancel"><X size={12} /></Button>
                          </div>
                        </div>
                        <p className="text-[10px] text-zinc-400">Sem preço? O SousChef estima o valor de mercado automaticamente (badge "Preço estimado").</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {totalCount > 0 && matchedCount < totalCount && (
                <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                  <Warning size={12} /> {totalCount - matchedCount} sem vínculo — serão criados automaticamente com preço estimado pelo SousChef IA
                </p>
              )}
            </div>

            {editData.modo_preparo && (
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Modo de Preparo</Label>
                <textarea value={editData.modo_preparo} onChange={(e) => setEditData({ ...editData, modo_preparo: e.target.value })} className="mt-1.5 w-full h-24 px-3 py-2 border border-zinc-200 rounded-md text-sm resize-none focus:outline-none focus:ring-1 focus:ring-zinc-400" data-testid="recipe-import-preparo" />
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setStep("upload")} className="flex-1">Voltar</Button>
              <Button onClick={handleConfirm} disabled={!editData.name.trim() || totalCount === 0} className="flex-1 bg-zinc-950 text-white hover:bg-zinc-800" data-testid="recipe-import-confirm-btn">
                Criar Ficha Técnica
              </Button>
            </div>
          </div>
        )}

        {/* STEP 4: Saving */}
        {step === "saving" && (
          <div className="flex flex-col items-center justify-center py-12">
            <SpinnerGap size={48} className="text-zinc-400 animate-spin mb-4" />
            <p className="text-zinc-600 font-medium">Criando ficha técnica...</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
